/**
 * Pattern of Life Report Viewer
 *
 * Renders pol_report jobs (analysis_type === 'pol_report'). These jobs are
 * built by PolReportService from precomputed POL tables, and the entire
 * payload arrives in job.results_summary.pol_report:
 *
 *   { entities, sections: { repeated_routes, dwells, anomalies, patterns,
 *     correlations, sequences, digital, atypicality }, section_counts,
 *     warnings, analysis_types, date_range, course_id }
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  LinearProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Route as RouteIcon,
  PlaceOutlined as DwellIcon,
  WarningAmberOutlined as AnomalyIcon,
  EventRepeat as PatternIcon,
  Hub as CorrelationIcon,
  Timeline as SequenceIcon,
  Devices as DigitalIcon,
  QueryStats as AtypicalityIcon,
  Groups as EntitiesIcon,
  AutoAwesome as SummaryIcon,
  FileDownloadOutlined as ExportIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Cell,
} from 'recharts';

import {
  AnalysisJob,
  DwellLocation,
  Route as RouteType,
  PolAnalysisType,
  POL_ANALYSIS_TYPE_INFO,
} from '@signals/app-shell/types';
import EnhancedMapVisualizationGL from './EnhancedMapVisualizationGL';
import PolReportDashboard from './PolReportDashboard';
import {
  PolReportPayload,
  ANOMALY_TYPE_LABELS,
  SEVERITY_COLORS,
  SEVERITY_RANK,
  formatDurationSeconds,
  formatDistanceMeters,
  formatDay,
  formatDateTime,
  formatHour,
  PolEntity,
} from './polReportShared';

// Re-export so existing consumers (ExportReportDialog) keep working.
export type { PolReportPayload } from './polReportShared';
export {
  ANOMALY_TYPE_LABELS,
  formatDurationSeconds,
  formatDistanceMeters,
} from './polReportShared';

const ExportReportDialog = React.lazy(() => import('./ExportReportDialog'));

interface PolReportViewerProps {
  job: AnalysisJob;
}

// ---------------------------------------------------------------------------
// Display metadata
// ---------------------------------------------------------------------------

const SECTION_ICONS: Record<PolAnalysisType, React.ElementType> = {
  repeated_routes: RouteIcon,
  dwells: DwellIcon,
  anomalies: AnomalyIcon,
  patterns: PatternIcon,
  correlations: CorrelationIcon,
  sequences: SequenceIcon,
  digital: DigitalIcon,
  atypicality: AtypicalityIcon,
};

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const SCORE_HINTS: Record<string, string> = {
  Routine:
    'How concentrated the stops are. Higher means the same places are visited again and again. Computed as 1 minus distinct locations divided by dwells.',
  Mobility:
    'How much ground the entity covered. Reaches 100% at about 60 miles of travel. Many distinct stop locations raise it as well.',
  Social:
    'Calls, texts, and distinct contacts. The dwell list does not change this score.',
  Anomaly:
    'How far this window departs from the entity\'s own baseline. Detector events are the individual breaks in routine.',
};

const ScoreBar: React.FC<{ label: string; value: number; color?: string }> = ({
  label,
  value,
  color,
}) => (
  <Box sx={{ mb: 0.75 }}>
    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
      <Tooltip title={SCORE_HINTS[label] || ''}>
        <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help', borderBottom: '1px dotted', borderColor: 'divider' }}>
          {label}
        </Typography>
      </Tooltip>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {Math.round(value * 100)}%
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={Math.min(100, Math.max(0, value * 100))}
      sx={{
        height: 6,
        borderRadius: 3,
        ...(color && { '& .MuiLinearProgress-bar': { bgcolor: color } }),
      }}
    />
  </Box>
);

/** Generic paginated section table. */
const SectionTable: React.FC<{
  columns: Array<{ key: string; label: string; align?: 'left' | 'right' | 'center' }>;
  rows: any[];
  renderRow: (row: any, index: number) => React.ReactNode;
}> = ({ columns, rows, renderRow }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const visible = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <TableContainer component={Card} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align || 'left'} sx={{ fontWeight: 700 }}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row, i) => renderRow(row, page * rowsPerPage + i))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 20, 50]}
        onPageChange={(_e, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Box>
  );
};

const parseAiSummary = (raw?: string | null): { lead: string; sections: Array<{ label: string; text: string }> } => {
  if (!raw?.trim()) return { lead: '', sections: [] };
  const parts = raw.split(/\s*\|\s*/);
  const sections: Array<{ label: string; text: string }> = [];
  let lead = '';
  parts.forEach((part, i) => {
    const m = part.match(/^([^:]{2,40}):\s*(.+)$/s);
    if (m) sections.push({ label: m[1].trim(), text: m[2].trim() });
    else if (i === 0) lead = part.trim();
    else sections.push({ label: 'Note', text: part.trim() });
  });
  return { lead, sections };
};

const truncateLabel = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** Clickable table row: highlight when this row is driving the map filter. */
const selectableRowSx = (selected: boolean) => ({
  cursor: 'pointer',
  bgcolor: selected ? 'action.selected' : 'transparent',
  '&:hover': {
    bgcolor: selected ? 'action.selected' : 'action.hover',
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PolReportViewer: React.FC<PolReportViewerProps> = ({ job }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [exportOpen, setExportOpen] = useState(false);
  const [warningsOpen, setWarningsOpen] = useState(false);
  // Clicking a route or dwell row filters the tab map to that item (same
  // selectedRouteId / selectedDwellId contract as EnhancedResultsViewer).
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
  const [selectedDwellId, setSelectedDwellId] = useState<number | null>(null);

  const clearMapFilter = () => {
    setSelectedRouteId(null);
    setSelectedDwellId(null);
  };

  const report: PolReportPayload | undefined = (job.results_summary as any)?.pol_report;

  const includedSections: PolAnalysisType[] = useMemo(() => {
    if (!report) return [];
    return POL_ANALYSIS_TYPE_INFO.map((t) => t.id).filter((id) =>
      report.analysis_types.includes(id)
    );
  }, [report]);

  const scoreVisibility = useMemo(() => {
    const have = new Set(includedSections);
    if (have.size === 0) {
      return { routine: true, mobility: true, social: true, anomaly: true, dwells: true, routes: true, contacts: true };
    }
    return {
      routine: ['dwells', 'repeated_routes', 'patterns', 'sequences'].some((id) => have.has(id as PolAnalysisType)),
      mobility: have.has('dwells') || have.has('repeated_routes'),
      social: have.has('digital'),
      anomaly: have.has('anomalies') || have.has('atypicality'),
      dwells: have.has('dwells'),
      routes: have.has('repeated_routes'),
      contacts: have.has('digital'),
    };
  }, [includedSections]);

  // Map POL routes/dwells onto the shared map component's expected shapes.
  const mapRoutes: RouteType[] = useMemo(() => {
    const rows = report?.sections?.repeated_routes || [];
    return rows
      .filter((r: any) => Array.isArray(r.route_geometry) && r.route_geometry.length >= 2)
      .map((r: any) => ({
        id: Number(r.id),
        route_name: r.route_name || `${r.origin?.name || 'Origin'} → ${r.destination?.name || 'Destination'}`,
        // POL stores GeoJSON [lon, lat]; map expects [lat, lng]
        path_coordinates: r.route_geometry.map((c: number[]) => [c[1], c[0]]),
        start_location: r.origin
          ? { lat: r.origin.lat, lng: r.origin.lon, name: r.origin.name }
          : undefined,
        end_location: r.destination
          ? { lat: r.destination.lat, lng: r.destination.lon, name: r.destination.name }
          : undefined,
        distance: r.avg_distance_meters,
        average_duration: r.avg_duration_seconds,
        frequency: r.usage_count,
        students: [r.entity_id],
        consistency_score: r.consistency_score,
        device_type: r.device_type,
      })) as unknown as RouteType[];
  }, [report]);

  const mapDwells: DwellLocation[] = useMemo(() => {
    const rows = report?.sections?.dwells || [];
    return rows
      .filter((d: any) => d.lat != null && d.lon != null)
      .map((d: any, i: number) => ({
        id: i + 1,
        center_latitude: d.lat,
        center_longitude: d.lon,
        radius: 50,
        total_visits: d.visit_count,
        total_duration: d.total_duration_seconds,
        average_duration: d.avg_duration_seconds,
        max_duration: d.total_duration_seconds,
        place_name: d.location_name,
        place_type: d.place_type,
        students: [d.entity_id],
        device_type: d.device_type,
      })) as unknown as DwellLocation[];
  }, [report]);

  if (!report) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        This Pattern of Life report has no payload. The job may have failed
        before results were stored — try re-running the report.
      </Alert>
    );
  }

  const tabs: Array<{
    id: 'summary' | 'entities' | PolAnalysisType;
    label: string;
    count?: number;
  }> = [
    { id: 'summary', label: 'Intelligence Summary' },
    { id: 'entities', label: 'Entities', count: report.entities.length },
    ...includedSections.map((id) => ({
      id,
      label: POL_ANALYSIS_TYPE_INFO.find((t) => t.id === id)?.label || id,
      count: report.section_counts?.[id] ?? report.sections?.[id]?.length ?? 0,
    })),
  ];

  const activeTabId = tabs[Math.min(tabValue, tabs.length - 1)]?.id || 'summary';

  // -------------------------------------------------------------------------
  // Section renderers
  // -------------------------------------------------------------------------

  const renderEntityProfile = (e: PolEntity) => {
    const parsed = parseAiSummary(e.ai_summary);
    const visibleAssessment = parsed.sections.filter((s) => {
      const label = s.label.toLowerCase();
      if (label === 'communication' && !scoreVisibility.social) return false;
      if (label.includes('anomal') && !scoreVisibility.anomaly) return false;
      return true;
    });
    const eid = e.entity_id;
    const routes = (report.sections?.repeated_routes || []).filter((r: any) => r.entity_id === eid);
    const dwells = (report.sections?.dwells || []).filter((d: any) => d.entity_id === eid);
    const anomalies = (report.sections?.anomalies || []).filter((a: any) => a.entity_id === eid);
    const patterns = (report.sections?.patterns || []).filter((p: any) => p.entity_id === eid);
    const correlations = (report.sections?.correlations || []).filter(
      (c: any) => c.entity_a_id === eid || c.entity_b_id === eid
    );
    const sequences = (report.sections?.sequences || []).filter((s: any) => s.entity_id === eid);
    const digital = (report.sections?.digital || []).filter((p: any) => p.entity_id === eid);
    const highAnoms = [...anomalies]
      .sort((a: any, b: any) => (SEVERITY_RANK[b.severity] || 0) - (SEVERITY_RANK[a.severity] || 0))
      .slice(0, 4);
    const topRoutes = [...routes].sort((a: any, b: any) => (b.usage_count || 0) - (a.usage_count || 0)).slice(0, 4);
    const topDwells = [...dwells].sort((a: any, b: any) => (b.visit_count || 0) - (a.visit_count || 0)).slice(0, 4);
    const hours = Array.isArray(e.active_hours) ? e.active_hours.slice(0, 6) : [];
    const days = Array.isArray(e.active_days) ? e.active_days.slice(0, 7) : [];
    const locations = Array.isArray(e.top_locations) ? e.top_locations.slice(0, 5) : [];

    return (
      <Card key={eid} variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'flex-start' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                {e.entity_id}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {e.entity_type || 'Entity'}
                {e.days_with_data != null ? ` · ${e.days_with_data} days with data` : ''}
                {e.data_confidence != null ? ` · ${Math.round(e.data_confidence * 100)}% data confidence` : ''}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {e.baseline_established === false && (
                <Tooltip title="Not enough history to establish a behavioral baseline">
                  <Chip label="Immature baseline" size="small" color="warning" variant="outlined" sx={{ height: 22 }} />
                </Tooltip>
              )}
              {e.baseline_established && (
                <Chip label="Mature baseline" size="small" color="success" variant="outlined" sx={{ height: 22 }} />
              )}
              {scoreVisibility.anomaly && e.anomaly_count > 0 && (
                <Chip label={`${e.anomaly_count} anomalies`} size="small" color="error" variant="outlined" sx={{ height: 22 }} />
              )}
            </Stack>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {scoreVisibility.routine && <Grid item xs={6} md={3}><ScoreBar label="Routine" value={e.routine_score} /></Grid>}
            {scoreVisibility.mobility && <Grid item xs={6} md={3}><ScoreBar label="Mobility" value={e.mobility_score} /></Grid>}
            {scoreVisibility.social && <Grid item xs={6} md={3}><ScoreBar label="Social" value={e.social_score} /></Grid>}
            {scoreVisibility.anomaly && <Grid item xs={6} md={3}><ScoreBar label="Anomaly" value={e.anomaly_score} color="#e57373" /></Grid>}
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {scoreVisibility.routine && scoreVisibility.mobility
              ? 'Routine is how often stops return to the same places. Mobility is how spread out those places are.'
              : 'Scores shown here are the ones that belong to the analysis types in this report.'}
            {scoreVisibility.social ? ' Social is calls and messages from digital activity.' : ''}
            {scoreVisibility.anomaly ? ' Anomaly is departure from this entity\'s baseline.' : ''}
          </Typography>

          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {scoreVisibility.dwells && <Chip label={`${e.total_dwells} dwells`} size="small" sx={{ height: 22 }} />}
            {scoreVisibility.dwells && <Chip label={`${e.unique_locations} locations`} size="small" sx={{ height: 22 }} />}
            {scoreVisibility.routes && <Chip label={`${e.total_routes} routes`} size="small" sx={{ height: 22 }} />}
            {scoreVisibility.mobility && <Chip label={`${(e.total_distance_km || 0).toFixed(0)} km traveled`} size="small" sx={{ height: 22 }} />}
            {scoreVisibility.contacts && <Chip label={`${e.unique_contacts} contacts`} size="small" sx={{ height: 22 }} />}
            {scoreVisibility.contacts && (e.total_sms || 0) > 0 && <Chip label={`${e.total_sms} SMS`} size="small" sx={{ height: 22 }} />}
            {scoreVisibility.contacts && (e.total_calls || 0) > 0 && <Chip label={`${e.total_calls} calls`} size="small" sx={{ height: 22 }} />}
          </Stack>

          {(parsed.lead || visibleAssessment.length > 0) && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <SummaryIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography variant="overline" sx={{ letterSpacing: 1, color: 'primary.main' }}>
                  AI Assessment
                </Typography>
              </Stack>
              {parsed.lead && (
                <Typography variant="body2" sx={{ mb: visibleAssessment.length ? 1 : 0 }}>
                  {parsed.lead}
                </Typography>
              )}
              {visibleAssessment.length > 0 && (
                <Grid container spacing={1.5}>
                  {visibleAssessment.map((s) => (
                    <Grid item xs={12} md={6} key={s.label}>
                      <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                        {s.label}
                      </Typography>
                      <Typography variant="body2">{s.text}</Typography>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Movement
              </Typography>
              {topRoutes.length === 0 && (
                <Typography variant="body2" color="text.secondary">No recurring routes in this window.</Typography>
              )}
              {topRoutes.map((r: any) => (
                <Typography key={r.id} variant="body2" sx={{ mt: 0.5 }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>{r.route_name || 'Unnamed route'}</Box>
                  {' · '}{r.usage_count || 0} trips · {Math.round((r.consistency_score || 0) * 100)}% consistent
                  {r.peak_day ? ` · ${r.peak_day}${r.peak_hour != null ? ` ${formatHour(r.peak_hour)}` : ''}` : ''}
                </Typography>
              ))}
              {hours.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {hours.map((h: any, i: number) => (
                    <Chip
                      key={i}
                      label={typeof h === 'number' ? formatHour(h) : String(h)}
                      size="small"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  ))}
                </Stack>
              )}
              {days.length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Active days: {days.map((d: any) => String(d)).join(', ')}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Places
              </Typography>
              {topDwells.length === 0 && locations.length === 0 && (
                <Typography variant="body2" color="text.secondary">No dwell locations in this window.</Typography>
              )}
              {(topDwells.length ? topDwells : locations).map((d: any, i: number) => (
                <Typography key={d.location_name || d.location_id || i} variant="body2" sx={{ mt: 0.5 }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>{d.location_name || d.name || 'Unknown'}</Box>
                  {d.visit_count != null ? ` · ${d.visit_count} visits` : d.visits != null ? ` · ${d.visits} visits` : ''}
                  {d.total_duration_seconds != null ? ` · ${formatDurationSeconds(d.total_duration_seconds)}` : ''}
                </Typography>
              ))}
              {sequences[0] && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Typical sequence: {(sequences[0].locations || []).map((l: any) => l.name || l.id).join(' → ') || '—'}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Events & associations
              </Typography>
              {highAnoms.length === 0 && patterns.length === 0 && correlations.length === 0 && (
                <Typography variant="body2" color="text.secondary">No notable events in this window.</Typography>
              )}
              {highAnoms.map((a: any) => (
                <Typography key={a.id} variant="body2" sx={{ mt: 0.5 }}>
                  <Chip
                    label={(a.severity || 'medium').toUpperCase()}
                    size="small"
                    color={SEVERITY_COLORS[a.severity] || 'default'}
                    sx={{ height: 18, mr: 0.75, fontSize: '0.6rem' }}
                  />
                  {ANOMALY_TYPE_LABELS[a.anomaly_type] || a.anomaly_type}
                  {a.anomaly_time ? ` · ${formatDay(a.anomaly_time)}` : ''}
                </Typography>
              ))}
              {patterns.slice(0, 3).map((p: any, i: number) => (
                <Typography key={p.id || i} variant="body2" sx={{ mt: 0.5 }}>
                  {(p.pattern_type || 'pattern').replace(/_/g, ' ')}
                  {p.location_name || p.route_name ? ` at ${p.location_name || p.route_name}` : ''}
                  {p.status ? ` · ${String(p.status).toLowerCase()}` : ''}
                </Typography>
              ))}
              {correlations.slice(0, 2).map((c: any, i: number) => (
                <Typography key={c.id || i} variant="body2" sx={{ mt: 0.5 }}>
                  {c.entity_a_id} ↔ {c.entity_b_id}
                  {' · '}{Math.round((c.correlation_strength || 0) * 100)}%
                  {c.correlation_type ? ` ${(c.correlation_type || '').replace(/_/g, ' ')}` : ''}
                </Typography>
              ))}
              {digital.length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {digital.length} digital pattern{digital.length === 1 ? '' : 's'} on file
                  {digital[0]?.name ? ` (top: ${digital[0].name})` : ''}
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderOverview = () => (
    <Stack spacing={2}>
      {report.entities.length === 0 && (
        <Alert severity="info">
          No entity behavioral summaries were found for this window.
        </Alert>
      )}
      {report.entities.map(renderEntityProfile)}
    </Stack>
  );

  const renderSectionChart = (mode: 'routes' | 'dwells') => {
    const atypicality = report.sections?.atypicality || [];
    const anomalies = report.sections?.anomalies || [];
    const dayBuckets = new Map<string, { label: string; atypical: number; events: number }>();
    atypicality.forEach((m: any) => {
      if (!m.day) return;
      const key = String(m.day).slice(0, 10);
      const cur = dayBuckets.get(key) || {
        label: new Date(`${key}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        atypical: 0,
        events: 0,
      };
      cur.atypical = Math.max(cur.atypical, m.atypicality_score || 0);
      dayBuckets.set(key, cur);
    });
    anomalies.forEach((a: any) => {
      if (!a.anomaly_time) return;
      const key = String(a.anomaly_time).slice(0, 10);
      const cur = dayBuckets.get(key) || {
        label: new Date(`${key}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        atypical: 0,
        events: 0,
      };
      cur.events += 1;
      dayBuckets.set(key, cur);
    });
    const timeline = Array.from(dayBuckets.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => v);

    if (timeline.length > 1) {
      return (
        <Card variant="outlined">
          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
              ACTIVITY TIMELINE
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <ComposedChart data={timeline} margin={{ top: 8, right: 4, bottom: 0, left: -22 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.08)} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} tickLine={false} />
                <YAxis
                  yAxisId="score"
                  domain={[0, 1]}
                  tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
                  tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis yAxisId="count" orientation="right" hide />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(value: any, name: any) =>
                    name === 'Atypicality' ? [`${Math.round(Number(value) * 100)}%`, name] : [value, name]
                  }
                />
                <Area
                  yAxisId="score"
                  type="monotone"
                  dataKey="atypical"
                  name="Atypicality"
                  stroke={theme.palette.primary.main}
                  fill={alpha(theme.palette.primary.main, 0.25)}
                  strokeWidth={2}
                />
                <Bar yAxisId="count" dataKey="events" name="Detector events" barSize={8}>
                  {timeline.map((d, i) => (
                    <Cell key={i} fill={alpha(theme.palette.error.main, d.events > 0 ? 0.7 : 0.15)} />
                  ))}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      );
    }

    const bars =
      mode === 'routes'
        ? (report.sections?.repeated_routes || [])
            .slice()
            .sort((a: any, b: any) => (b.usage_count || 0) - (a.usage_count || 0))
            .slice(0, 8)
            .map((r: any) => ({
              label: truncateLabel(r.route_name || `${r.origin?.name || '?'} → ${r.destination?.name || '?'}`, 22),
              value: r.usage_count || 0,
            }))
        : (report.sections?.dwells || [])
            .slice()
            .sort((a: any, b: any) => (b.visit_count || 0) - (a.visit_count || 0))
            .slice(0, 8)
            .map((d: any) => ({
              label: truncateLabel(d.location_name || 'Unknown', 22),
              value: d.visit_count || 0,
            }));

    if (bars.length === 0) return null;

    return (
      <Card variant="outlined">
        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
            {mode === 'routes' ? 'TRIP FREQUENCY' : 'VISIT FREQUENCY'}
          </Typography>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={bars} layout="vertical" margin={{ top: 4, right: 12, bottom: 0, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.08)} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} tickLine={false} />
              <YAxis
                type="category"
                dataKey="label"
                width={110}
                tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                tickLine={false}
                axisLine={false}
              />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Bar dataKey="value" name={mode === 'routes' ? 'Trips' : 'Visits'} fill={theme.palette.primary.main} radius={[0, 3, 3, 0]} barSize={12} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  };

  const renderRepeatedRoutes = () => {
    const rows = report.sections?.repeated_routes || [];
    return (
      <Grid container spacing={2}>
        {mapRoutes.length > 0 && (
          <Grid item xs={12} lg={5}>
            <Stack spacing={1.5}>
              {selectedRouteId != null && (
                <Chip
                  label="Map showing selected route"
                  size="small"
                  color="primary"
                  onDelete={clearMapFilter}
                  sx={{ alignSelf: 'flex-start' }}
                />
              )}
              <EnhancedMapVisualizationGL
                dwellLocations={[]}
                routes={mapRoutes}
                activeTab={2}
                mapHeight={480}
                compactControls
                selectedRouteId={selectedRouteId}
              />
              {renderSectionChart('routes')}
            </Stack>
          </Grid>
        )}
        <Grid item xs={12} lg={mapRoutes.length > 0 ? 7 : 12}>
          {mapRoutes.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Click a row to show only that route on the map. Click again to show all.
            </Typography>
          )}
          <SectionTable
          columns={[
            { key: 'entity', label: 'Entity' },
            { key: 'route', label: 'Route' },
            { key: 'usage', label: 'Trips', align: 'right' },
            { key: 'consistency', label: 'Consistency', align: 'right' },
            { key: 'peak', label: 'Peak Time' },
            { key: 'distance', label: 'Distance', align: 'right' },
            { key: 'duration', label: 'Avg Duration', align: 'right' },
            { key: 'shared', label: 'Shared With' },
          ]}
          rows={rows}
          renderRow={(r: any) => {
            const routeId = Number(r.id);
            const selected = selectedRouteId != null && selectedRouteId === routeId;
            return (
            <TableRow
              key={r.id}
              hover
              selected={selected}
              onClick={() => {
                if (selected) {
                  clearMapFilter();
                } else {
                  setSelectedDwellId(null);
                  setSelectedRouteId(Number.isFinite(routeId) ? routeId : null);
                }
              }}
              sx={selectableRowSx(selected)}
            >
              <TableCell>{r.entity_id}</TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {r.route_name || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(r.origin?.name || 'Origin')} → {(r.destination?.name || 'Destination')}
                </Typography>
              </TableCell>
              <TableCell align="right">{r.usage_count}</TableCell>
              <TableCell align="right">{Math.round((r.consistency_score || 0) * 100)}%</TableCell>
              <TableCell>
                {r.peak_day || ''} {r.peak_hour != null ? `${r.peak_hour}:00` : ''}
              </TableCell>
              <TableCell align="right">{formatDistanceMeters(r.avg_distance_meters)}</TableCell>
              <TableCell align="right">{formatDurationSeconds(r.avg_duration_seconds)}</TableCell>
              <TableCell>
                {(r.shared_with_entities || []).length > 0 ? (
                  <Chip
                    label={`${(r.shared_with_entities || []).length} entities`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ height: 22 }}
                  />
                ) : (
                  '—'
                )}
              </TableCell>
            </TableRow>
            );
          }}
          />
        </Grid>
      </Grid>
    );
  };

  const renderDwells = () => {
    const rows = report.sections?.dwells || [];
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            Each row is one place. Routine is how often these stops return to the same places. Mobility is how spread out the places are.
          </Typography>
        </Grid>
        {mapDwells.length > 0 && (
          <Grid item xs={12} lg={5}>
            <Stack spacing={1.5}>
              {selectedDwellId != null && (
                <Chip
                  label="Map showing selected dwell"
                  size="small"
                  color="primary"
                  onDelete={clearMapFilter}
                  sx={{ alignSelf: 'flex-start' }}
                />
              )}
              <EnhancedMapVisualizationGL
                dwellLocations={mapDwells}
                routes={[]}
                activeTab={1}
                mapHeight={480}
                compactControls
                selectedDwellId={selectedDwellId}
              />
              {renderSectionChart('dwells')}
            </Stack>
          </Grid>
        )}
        <Grid item xs={12} lg={mapDwells.length > 0 ? 7 : 12}>
          {mapDwells.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Click a row to show only that dwell on the map. Click again to show all.
            </Typography>
          )}
          <SectionTable
          columns={[
            { key: 'entity', label: 'Entity' },
            { key: 'location', label: 'Location' },
            { key: 'type', label: 'Place Type' },
            { key: 'visits', label: 'Visits', align: 'right' },
            { key: 'total', label: 'Total Time', align: 'right' },
            { key: 'avg', label: 'Avg Stay', align: 'right' },
            { key: 'first', label: 'First Visit' },
            { key: 'last', label: 'Last Visit' },
            { key: 'device', label: 'Device' },
          ]}
          rows={rows}
          renderRow={(d: any, i: number) => {
            const dwellId = i + 1;
            const selected = selectedDwellId === dwellId;
            return (
            <TableRow
              key={`${d.entity_id}-${i}`}
              hover
              selected={selected}
              onClick={() => {
                if (selected) {
                  clearMapFilter();
                } else {
                  setSelectedRouteId(null);
                  setSelectedDwellId(dwellId);
                }
              }}
              sx={selectableRowSx(selected)}
            >
              <TableCell>{d.entity_id}</TableCell>
              <TableCell>{d.location_name || '—'}</TableCell>
              <TableCell>{d.place_type || '—'}</TableCell>
              <TableCell align="right">{d.visit_count}</TableCell>
              <TableCell align="right">{formatDurationSeconds(d.total_duration_seconds)}</TableCell>
              <TableCell align="right">{formatDurationSeconds(d.avg_duration_seconds)}</TableCell>
              <TableCell>{formatDay(d.first_visit)}</TableCell>
              <TableCell>{formatDay(d.last_visit)}</TableCell>
              <TableCell>
                <Chip
                  label={(d.device_type || 'unknown').replace(/_/g, ' ')}
                  size="small"
                  sx={{ height: 22, textTransform: 'capitalize' }}
                />
              </TableCell>
            </TableRow>
            );
          }}
          />
        </Grid>
      </Grid>
    );
  };

  const renderAnomalies = () => {
    const rows = report.sections?.anomalies || [];
    return (
      <SectionTable
        columns={[
          { key: 'entity', label: 'Entity' },
          { key: 'type', label: 'Type' },
          { key: 'severity', label: 'Severity' },
          { key: 'time', label: 'When' },
          { key: 'expected', label: 'Expected' },
          { key: 'actual', label: 'Actual' },
          { key: 'deviation', label: 'Deviation', align: 'right' },
          { key: 'ack', label: 'Acknowledged' },
        ]}
        rows={rows}
        renderRow={(a: any) => (
          <TableRow key={a.id} hover>
            <TableCell>{a.entity_id}</TableCell>
            <TableCell>
              <Chip
                label={ANOMALY_TYPE_LABELS[a.anomaly_type] || a.anomaly_type}
                size="small"
                variant="outlined"
                sx={{ height: 22 }}
              />
            </TableCell>
            <TableCell>
              <Chip
                label={a.severity}
                size="small"
                color={SEVERITY_COLORS[a.severity] || 'default'}
                sx={{ height: 22, textTransform: 'capitalize' }}
              />
            </TableCell>
            <TableCell>{formatDateTime(a.anomaly_time)}</TableCell>
            <TableCell sx={{ maxWidth: 220 }}>
              <Typography variant="caption">{a.expected_behavior || '—'}</Typography>
            </TableCell>
            <TableCell sx={{ maxWidth: 220 }}>
              <Typography variant="caption">{a.actual_behavior || '—'}</Typography>
            </TableCell>
            <TableCell align="right">
              {a.deviation_score != null ? a.deviation_score.toFixed(2) : '—'}
            </TableCell>
            <TableCell>{a.is_acknowledged ? 'Yes' : 'No'}</TableCell>
          </TableRow>
        )}
      />
    );
  };

  const renderPatterns = () => {
    const rows = report.sections?.patterns || [];
    return (
      <SectionTable
        columns={[
          { key: 'entity', label: 'Entity' },
          { key: 'type', label: 'Type' },
          { key: 'what', label: 'Pattern' },
          { key: 'freq', label: 'Frequency' },
          { key: 'window', label: 'Time Window' },
          { key: 'occ', label: 'Occurrences', align: 'right' },
          { key: 'adherence', label: 'Adherence', align: 'right' },
          { key: 'confidence', label: 'Confidence', align: 'right' },
          { key: 'status', label: 'Status' },
        ]}
        rows={rows}
        renderRow={(p: any) => (
          <TableRow key={p.id} hover>
            <TableCell>{p.entity_id}</TableCell>
            <TableCell sx={{ textTransform: 'capitalize' }}>
              {(p.pattern_type || '').replace(/_/g, ' ')}
            </TableCell>
            <TableCell>{p.location_name || p.route_name || '—'}</TableCell>
            <TableCell sx={{ textTransform: 'capitalize' }}>
              {String(p.frequency || '').toLowerCase()}
              {Array.isArray(p.days_of_week) && p.days_of_week.length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {p.days_of_week.join(', ')}
                </Typography>
              )}
            </TableCell>
            <TableCell>
              {p.time_window_start && p.time_window_end
                ? `${p.time_window_start} – ${p.time_window_end}`
                : '—'}
            </TableCell>
            <TableCell align="right">{p.occurrence_count}</TableCell>
            <TableCell align="right">
              {p.adherence_rate != null ? `${Math.round(p.adherence_rate * 100)}%` : '—'}
            </TableCell>
            <TableCell align="right">
              {p.confidence_score != null ? `${Math.round(p.confidence_score * 100)}%` : '—'}
            </TableCell>
            <TableCell>
              <Chip
                label={String(p.status || '').toLowerCase()}
                size="small"
                color={
                  String(p.status).toLowerCase() === 'breaking'
                    ? 'error'
                    : String(p.status).toLowerCase() === 'emerging'
                      ? 'info'
                      : 'default'
                }
                sx={{ height: 22, textTransform: 'capitalize' }}
              />
            </TableCell>
          </TableRow>
        )}
      />
    );
  };

  const renderCorrelations = () => {
    const rows = report.sections?.correlations || [];
    return (
      <SectionTable
        columns={[
          { key: 'pair', label: 'Entities' },
          { key: 'type', label: 'Type' },
          { key: 'strength', label: 'Strength' },
          { key: 'occ', label: 'Occurrences', align: 'right' },
          { key: 'shared', label: 'Shared Locations', align: 'right' },
          { key: 'overlap', label: 'Avg Overlap', align: 'right' },
          { key: 'last', label: 'Last Observed' },
        ]}
        rows={rows}
        renderRow={(c: any) => (
          <TableRow key={c.id} hover>
            <TableCell>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {c.entity_a_id} ↔ {c.entity_b_id}
              </Typography>
            </TableCell>
            <TableCell sx={{ textTransform: 'capitalize' }}>
              {(c.correlation_type || '').replace(/_/g, ' ')}
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, (c.correlation_strength || 0) * 100)}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary">
                {Math.round((c.correlation_strength || 0) * 100)}%
              </Typography>
            </TableCell>
            <TableCell align="right">{c.occurrence_count}</TableCell>
            <TableCell align="right">{(c.shared_locations || []).length || '—'}</TableCell>
            <TableCell align="right">
              {c.avg_overlap_duration_minutes != null
                ? `${Math.round(c.avg_overlap_duration_minutes)}m`
                : '—'}
            </TableCell>
            <TableCell>{formatDay(c.last_observed)}</TableCell>
          </TableRow>
        )}
      />
    );
  };

  const renderSequences = () => {
    const rows = report.sections?.sequences || [];
    return (
      <SectionTable
        columns={[
          { key: 'entity', label: 'Entity' },
          { key: 'chain', label: 'Sequence' },
          { key: 'occ', label: 'Occurrences', align: 'right' },
          { key: 'duration', label: 'Avg Duration', align: 'right' },
          { key: 'start', label: 'Typical Start' },
          { key: 'days', label: 'Typical Days' },
        ]}
        rows={rows}
        renderRow={(s: any) => (
          <TableRow key={s.id} hover>
            <TableCell>{s.entity_id}</TableCell>
            <TableCell>
              <Typography variant="body2">
                {(s.locations || [])
                  .map((l: any) => l.name || l.id || '?')
                  .join(' → ')}
              </Typography>
            </TableCell>
            <TableCell align="right">{s.occurrence_count}</TableCell>
            <TableCell align="right">
              {s.avg_total_duration_minutes != null
                ? `${Math.round(s.avg_total_duration_minutes)}m`
                : '—'}
            </TableCell>
            <TableCell>
              {s.typical_start_hour != null ? `${s.typical_start_hour}:00` : '—'}
            </TableCell>
            <TableCell>{(s.typical_days || []).join(', ') || '—'}</TableCell>
          </TableRow>
        )}
      />
    );
  };

  const renderDigital = () => {
    const rows = report.sections?.digital || [];
    return (
      <SectionTable
        columns={[
          { key: 'entity', label: 'Entity' },
          { key: 'category', label: 'Category' },
          { key: 'name', label: 'Pattern' },
          { key: 'desc', label: 'Description' },
          { key: 'peak', label: 'Peak Time' },
          { key: 'occ', label: 'Occurrences', align: 'right' },
          { key: 'confidence', label: 'Confidence', align: 'right' },
        ]}
        rows={rows}
        renderRow={(p: any) => (
          <TableRow key={p.id} hover>
            <TableCell>{p.entity_id}</TableCell>
            <TableCell>
              <Chip
                label={(p.category || '').replace(/_/g, ' ')}
                size="small"
                variant="outlined"
                sx={{ height: 22, textTransform: 'capitalize' }}
              />
            </TableCell>
            <TableCell>{p.name || '—'}</TableCell>
            <TableCell sx={{ maxWidth: 280 }}>
              <Typography variant="caption">{p.description || '—'}</Typography>
            </TableCell>
            <TableCell>
              {p.peak_day || ''} {p.peak_hour != null ? `${p.peak_hour}:00` : ''}
            </TableCell>
            <TableCell align="right">{p.occurrence_count}</TableCell>
            <TableCell align="right">{Math.round((p.confidence || 0) * 100)}%</TableCell>
          </TableRow>
        )}
      />
    );
  };

  const renderAtypicality = () => {
    const rows = report.sections?.atypicality || [];
    return (
      <SectionTable
        columns={[
          { key: 'entity', label: 'Entity' },
          { key: 'day', label: 'Day' },
          { key: 'score', label: 'Atypicality' },
          { key: 'anoms', label: 'Detector Events' },
          { key: 'distance', label: 'Distance', align: 'right' },
          { key: 'places', label: 'Places', align: 'right' },
          { key: 'dwells', label: 'Dwells', align: 'right' },
        ]}
        rows={rows}
        renderRow={(m: any, i: number) => (
          <TableRow key={`${m.entity_id}-${m.day}-${i}`} hover>
            <TableCell>{m.entity_id}</TableCell>
            <TableCell>{formatDay(m.day)}</TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, (m.atypicality_score || 0) * 100)}
                color={
                  (m.atypicality_score || 0) >= 0.7
                    ? 'error'
                    : (m.atypicality_score || 0) >= 0.4
                      ? 'warning'
                      : 'primary'
                }
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary">
                {((m.atypicality_score || 0) * 100).toFixed(0)}%
              </Typography>
            </TableCell>
            <TableCell>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {Object.entries(m.anomaly_breakdown || {})
                  .filter(([, count]) => (count as number) > 0)
                  .map(([type, count]) => (
                    <Chip
                      key={type}
                      label={`${ANOMALY_TYPE_LABELS[type] || type}: ${count}`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  ))}
                {m.total_anomaly_count === 0 && '—'}
              </Stack>
            </TableCell>
            <TableCell align="right">
              {m.distance_traveled_km != null ? `${m.distance_traveled_km.toFixed(1)} km` : '—'}
            </TableCell>
            <TableCell align="right">{m.unique_places_visited}</TableCell>
            <TableCell align="right">{m.dwell_count}</TableCell>
          </TableRow>
        )}
      />
    );
  };

  const SECTION_RENDERERS: Record<PolAnalysisType, () => React.ReactNode> = {
    repeated_routes: renderRepeatedRoutes,
    dwells: renderDwells,
    anomalies: renderAnomalies,
    patterns: renderPatterns,
    correlations: renderCorrelations,
    sequences: renderSequences,
    digital: renderDigital,
    atypicality: renderAtypicality,
  };

  return (
    <Box>
      {/* Report header */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
            spacing={1}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Established Pattern of Life
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {report.course_id ? `${report.course_id} · ` : ''}
                {formatDay(report.date_range.start)} – {formatDay(report.date_range.end)} ·
                generated {formatDateTime(report.generated_at)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                label={`${report.entities.length} entities`}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`${includedSections.length} analysis types`}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<ExportIcon />}
                onClick={() => setExportOpen(true)}
              >
                Export Formal Report
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {exportOpen && (
        <React.Suspense fallback={null}>
          <ExportReportDialog
            open={exportOpen}
            onClose={() => setExportOpen(false)}
            job={job}
            report={report}
          />
        </React.Suspense>
      )}

      {/* Data notes — compact, expandable so they don't dominate the report */}
      {report.warnings.length > 0 && (
        <Alert
          severity="info"
          variant="outlined"
          icon={false}
          sx={{ mb: 2, py: 0.25, '& .MuiAlert-message': { width: '100%', py: 0.5 } }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            onClick={() => setWarningsOpen((v) => !v)}
            sx={{ cursor: 'pointer', userSelect: 'none' }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {report.warnings.length} data note{report.warnings.length === 1 ? '' : 's'} about
              coverage for this window
            </Typography>
            {warningsOpen ? (
              <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            )}
          </Stack>
          <Collapse in={warningsOpen}>
            <Box sx={{ pt: 0.5 }}>
              {report.warnings.map((w, i) => (
                <Typography key={i} variant="caption" display="block" color="text.secondary">
                  • {w}
                </Typography>
              ))}
            </Box>
          </Collapse>
        </Alert>
      )}

      {/* Section tabs */}
      <Tabs
        value={Math.min(tabValue, tabs.length - 1)}
        onChange={(_e, v) => {
          setTabValue(v);
          clearMapFilter();
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        {tabs.map((t) => {
          const Icon =
            t.id === 'summary'
              ? SummaryIcon
              : t.id === 'entities'
                ? EntitiesIcon
                : SECTION_ICONS[t.id as PolAnalysisType];
          return (
            <Tab
              key={t.id}
              iconPosition="start"
              icon={<Icon sx={{ fontSize: 18 }} />}
              label={
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <span>{t.label}</span>
                  {t.count != null && (
                    <Chip label={t.count} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Stack>
              }
              sx={{ minHeight: 48, textTransform: 'none' }}
            />
          );
        })}
      </Tabs>

      <Box sx={{ pt: 1 }}>
        {activeTabId === 'summary' ? (
          <PolReportDashboard report={report} mapDwells={mapDwells} mapRoutes={mapRoutes} />
        ) : activeTabId === 'entities' ? (
          renderOverview()
        ) : (
          SECTION_RENDERERS[activeTabId as PolAnalysisType]()
        )}
      </Box>
    </Box>
  );
};

export default PolReportViewer;
